import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { jobService } from '../services/jobService';
import JobDetailsModal from './JobDetailsModal';
import { SideNav } from './SideNav';
import { useAuth } from '../context/AuthContext';
import { PageLayout } from './ui';

function JobTracker() {
  const { sideNavLocked, setSideNavLocked } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jobDetailsModalOpen, setJobDetailsModalOpen] = useState(false);
  const [selectedStage, setSelectedStage] = useState('wishlist');
  const [viewingJob, setViewingJob] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Company logo mapping for popular companies
  const getCompanyLogo = (companyName) => {
    if (!companyName) return null;
    
    const company = companyName.toLowerCase().trim();
    
    // Popular company logos mapping
    const logoMap = {
      'google': 'https://logo.clearbit.com/google.com',
      'meta': 'https://logo.clearbit.com/meta.com',
      'facebook': 'https://logo.clearbit.com/facebook.com',
      'apple': 'https://logo.clearbit.com/apple.com',
      'microsoft': 'https://logo.clearbit.com/microsoft.com',
      'amazon': 'https://logo.clearbit.com/amazon.com',
      'netflix': 'https://logo.clearbit.com/netflix.com',
      'tesla': 'https://logo.clearbit.com/tesla.com',
      'spotify': 'https://logo.clearbit.com/spotify.com',
      'uber': 'https://logo.clearbit.com/uber.com',
      'airbnb': 'https://logo.clearbit.com/airbnb.com',
      'stripe': 'https://logo.clearbit.com/stripe.com',
      'slack': 'https://logo.clearbit.com/slack.com',
      'salesforce': 'https://logo.clearbit.com/salesforce.com',
      'oracle': 'https://logo.clearbit.com/oracle.com',
      'ibm': 'https://logo.clearbit.com/ibm.com',
      'linkedin': 'https://logo.clearbit.com/linkedin.com',
      'twitter': 'https://logo.clearbit.com/twitter.com',
      'x': 'https://logo.clearbit.com/x.com',
      'shopify': 'https://logo.clearbit.com/shopify.com',
      'dropbox': 'https://logo.clearbit.com/dropbox.com',
      'adobe': 'https://logo.clearbit.com/adobe.com',
      'zoom': 'https://logo.clearbit.com/zoom.us',
      'palantir': 'https://logo.clearbit.com/palantir.com',
      'coinbase': 'https://logo.clearbit.com/coinbase.com',
      'openai': 'https://logo.clearbit.com/openai.com'
    };

    // Check for exact match first
    if (logoMap[company]) {
      return logoMap[company];
    }

    // Check for partial matches
    for (const [key, logo] of Object.entries(logoMap)) {
      if (company.includes(key) || key.includes(company)) {
        return logo;
      }
    }

    // Try clearbit generic approach for other companies
    // Remove common suffixes and clean up the name
    const cleanCompany = company
      .replace(/\s+(inc|corp|corporation|ltd|limited|llc|co|company)\.?$/i, '')
      .replace(/[^a-z0-9]/g, '');
    
    return `https://logo.clearbit.com/${cleanCompany}.com`;
  };

  // Generate company initials as fallback
  const getCompanyInitials = (companyName) => {
    if (!companyName) return '?';
    return companyName
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
  };

  // Kanban columns - updated with better colors for dark theme
  const columns = useMemo(() => [
    { id: 'wishlist', title: 'Wishlist', color: '#8B5CF6' },
    { id: 'applied', title: 'Applied', color: '#3B82F6' },
    { id: 'interview', title: 'Interview', color: '#F59E0B' },
    { id: 'offer', title: 'Offer', color: '#10B981' },
    { id: 'rejected', title: 'Rejected', color: '#EF4444' }
  ], []);

  // Load jobs from API
  const loadJobs = useCallback(async () => {
    try {
      setLoading(true);
      const jobsData = await jobService.getAllJobs();
      setJobs(jobsData);
    } catch (error) {
      console.error('Error loading jobs:', error);
      showSnackbar('Error loading jobs', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  // Memoized count calculations that will update when jobs array changes
  const jobCounts = useMemo(() => {
    const counts = {};
    columns.forEach(column => {
      counts[column.id] = jobs.filter(job => job.stage === column.id).length;
    });
    return counts;
  }, [jobs, columns]);

  // Memoized job lists by stage to prevent changes during drag
  const jobsByStage = useMemo(() => {
    const jobsMap = {};
    columns.forEach(column => {
      jobsMap[column.id] = jobs
        .filter(job => job.stage === column.id)
        .sort((a, b) => (a.position || 0) - (b.position || 0));
    });
    return jobsMap;
  }, [jobs, columns]);

  const getJobsByStatus = (status) => {
    return jobsByStage[status] || [];
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: '', severity: 'info' });
  };

  const handleAddJob = (stage) => {
    setSelectedStage(stage);
    setViewingJob(null); // null means new job
    setJobDetailsModalOpen(true);
  };

  const handleEditJob = (job) => {
    setViewingJob(job);
    setJobDetailsModalOpen(true);
  };

  const handleDeleteJob = async (jobId) => {
    try {
      await jobService.deleteJob(jobId);
      setJobs(prev => prev.filter(job => job._id !== jobId));
      showSnackbar('Job deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting job:', error);
      showSnackbar('Error deleting job', 'error');
    }
  };

  const handleCreateJob = async (jobData) => {
    try {
      const newJob = await jobService.createJob(jobData);
      setJobs(prev => [...prev, newJob]);
      showSnackbar('Job added successfully!', 'success');
      return newJob; // Return the created job
    } catch (error) {
      console.error('Error creating job:', error);
      showSnackbar('Error creating job', 'error');
      throw error;
    }
  };

  const handleUpdateJob = async (jobId, jobData) => {
    try {
      const updatedJob = await jobService.updateJob(jobId, jobData);
      setJobs(prev => prev.map(job => 
        job._id === jobId ? updatedJob : job
      ));
      // No success notification - visual update is enough feedback
      return updatedJob; // Return the updated job
    } catch (error) {
      console.error('Error updating job:', error);
      showSnackbar('Error updating job', 'error');
      throw error;
    }
  };

  const handleJobSubmit = async (jobIdOrData, jobData = null) => {
    // If jobData is null, this is new job creation
    if (jobData === null) {
      return await handleCreateJob(jobIdOrData);
    } else {
      // This is an auto-save update for an existing job
      return await handleUpdateJob(jobIdOrData, jobData);
    }
  };

  const handleCloseJobDetailsModal = () => {
    setJobDetailsModalOpen(false);
    setViewingJob(null);
  };

  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;

    // If no destination, do nothing
    if (!destination) {
      return;
    }

    // If dropped in same position, do nothing
    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    const sourceStage = source.droppableId;
    const destStage = destination.droppableId;
    const sourceIndex = source.index;
    const destIndex = destination.index;
    
    // OPTIMISTIC UPDATE: Reorder jobs in UI immediately
    const previousJobs = [...jobs];
    
    if (sourceStage === destStage) {
      // Reordering within the same column
      const columnJobs = jobsByStage[sourceStage];
      const reorderedJobs = Array.from(columnJobs);
      const [removed] = reorderedJobs.splice(sourceIndex, 1);
      reorderedJobs.splice(destIndex, 0, removed);
      
      // Update the jobs array with new positions
      const newJobs = jobs.map(job => {
        if (job.stage === sourceStage) {
          const newIndex = reorderedJobs.findIndex(j => j._id === job._id);
          return { ...job, position: newIndex };
        }
        return job;
      });
      
      setJobs(newJobs);
    } else {
      // Moving between different columns
      const newJobs = jobs.map(job => {
        if (job.stage === sourceStage && job._id !== draggableId) {
          // Update positions in source column (close gaps)
          const sourceJobs = jobsByStage[sourceStage];
          const jobIndex = sourceJobs.findIndex(j => j._id === job._id);
          if (jobIndex > sourceIndex) {
            return { ...job, position: job.position - 1 };
          }
        } else if (job.stage === destStage) {
          // Update positions in destination column (make space)
          if (job.position >= destIndex) {
            return { ...job, position: job.position + 1 };
          }
        } else if (job._id === draggableId) {
          // Update the dragged job
          return { ...job, stage: destStage, position: destIndex };
        }
        return job;
      });
      
      setJobs(newJobs);
    }
    
    try {
      // Update backend with position information
      await jobService.updateJobStage(draggableId, destStage, sourceIndex, destIndex);
      
    } catch (error) {
      console.error('Error updating job stage:', error);
      
      // ROLLBACK: Restore previous state if API fails
      setJobs(previousJobs);
      
      showSnackbar('Error moving job. Please try again.', 'error');
    }
  };

  const JobCard = ({ job, index }) => {
    const [logoError, setLogoError] = useState(false);
    const companyLogo = getCompanyLogo(job.company);
    const companyInitials = getCompanyInitials(job.company);

    const handleEditClick = (e) => {
      e.stopPropagation();
      e.preventDefault();
      handleEditJob(job);
    };

    const handleDeleteClick = (e) => {
      e.stopPropagation();
      e.preventDefault();
      if (window.confirm(`Are you sure you want to delete the ${job.jobTitle} position at ${job.company}?`)) {
        handleDeleteJob(job._id);
      }
    };

    return (
      <Draggable draggableId={job._id} index={index}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            className={`mb-3 p-4 rounded-lg border border-gray-200 border-opacity-20 min-h-[80px] cursor-grab transition-all duration-200 group relative ${
              snapshot.isDragging 
                ? 'bg-white bg-opacity-20 border-blue-400 shadow-lg scale-105' 
                : 'bg-white bg-opacity-5 hover:bg-opacity-10'
            }`}
            style={{
              ...provided.draggableProps.style,
              cursor: snapshot.isDragging ? 'grabbing' : 'grab'
            }}
          >
            {/* Main content area */}
            <div className="flex items-start gap-3 flex-1">
              {/* Company Logo/Avatar */}
              <div className="w-10 h-10 rounded-lg bg-gray-700 flex items-center justify-center flex-shrink-0 overflow-hidden">
                {companyLogo && !logoError ? (
                  <img 
                    src={companyLogo}
                    alt={`${job.company} logo`}
                    className="w-full h-full object-contain"
                    onError={() => setLogoError(true)}
                  />
                ) : (
                  <span className="text-white text-sm font-semibold">
                    {companyInitials}
                  </span>
                )}
              </div>
              
              {/* Content */}
              <div className="flex-1 min-w-0">
                <div 
                  className="font-medium text-white mb-1 cursor-pointer hover:text-blue-300 transition-colors duration-200 line-clamp-2"
                  onClick={handleEditClick}
                  title={job.jobTitle}
                >
                  {job.jobTitle}
                </div>
                <div 
                  className="text-gray-300 text-sm cursor-pointer hover:text-blue-300 transition-colors duration-200 truncate"
                  onClick={handleEditClick}
                  title={job.company}
                >
                  {job.company}
                </div>
              </div>

              {/* Action buttons - aligned vertically on the right */}
              <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-all duration-200">
                {/* Edit button */}
                <button
                  onClick={handleEditClick}
                  className="p-1.5 rounded hover:bg-white hover:bg-opacity-10 transition-all duration-200"
                  title="Edit job"
                >
                  <svg className="w-4 h-4 text-gray-400 hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                
                {/* Delete button */}
                <button
                  onClick={handleDeleteClick}
                  className="p-1.5 rounded hover:bg-red-500 hover:bg-opacity-20 transition-all duration-200"
                  title="Delete job"
                >
                  <svg className="w-4 h-4 text-red-400 hover:text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
      </Draggable>
    );
  };

  const AddJobButton = ({ stage }) => (
    <button
      onClick={() => handleAddJob(stage)}
      className="w-full p-3 border-2 border-dashed border-gray-600 rounded-lg text-gray-400 hover:border-blue-500 hover:text-blue-400 hover:bg-blue-500 hover:bg-opacity-10 transition-all duration-200 text-sm font-medium flex items-center justify-center gap-2"
    >
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
      </svg>
      Add Job
    </button>
  );

  if (loading) {
    return (
      <>
        <SideNav onLockChange={setSideNavLocked} />
        <div className={`min-h-screen transition-all duration-300 ${sideNavLocked ? 'ml-64' : 'ml-0'}`}>
          <PageLayout>
            <div className={`py-8 px-6 ${!sideNavLocked ? 'ml-16' : ''}`}>
              <div className="text-white text-center">Loading jobs...</div>
            </div>
          </PageLayout>
        </div>
      </>
    );
  }

  return (
    <>
      <SideNav onLockChange={setSideNavLocked} />
      <div className={`min-h-screen transition-all duration-300 ${sideNavLocked ? 'ml-64' : 'ml-0'}`}>
        <PageLayout>
          {/* Header */}
          <div className={`py-8 px-6 ${!sideNavLocked ? 'ml-16' : ''}`}>
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-white mb-2">Pipeline</h1>
            </div>
          </div>

          {/* Kanban Board - Full Width */}
          <div className="px-6 pb-8">
            <DragDropContext onDragEnd={handleDragEnd}>
              <div className="flex gap-6 overflow-x-auto min-h-[calc(100vh-200px)] pb-4">
                {columns.map((column) => (
                  <Droppable droppableId={column.id} key={column.id}>
                    {(provided, snapshot) => (
                      <div className="w-80 flex-shrink-0">
                        {/* Column Header - Dark theme */}
                        <div className="bg-white bg-opacity-10 rounded-t-lg border border-gray-200 border-opacity-20 p-4">
                          <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-white flex items-center gap-3">
                              <span 
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: column.color }}
                              />
                              {column.title}
                            </h3>
                            <div 
                              className="text-white rounded-full px-3 py-1 text-sm font-medium"
                              style={{ backgroundColor: column.color }}
                            >
                              {jobCounts[column.id] || 0}
                            </div>
                          </div>
                        </div>

                        {/* Column Content - Dark theme */}
                        <div 
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={`bg-white bg-opacity-5 rounded-b-lg border-l border-r border-b border-gray-200 border-opacity-20 p-4 min-h-[500px] transition-colors duration-200 ${
                            snapshot.isDraggingOver ? 'bg-opacity-10' : ''
                          }`}
                        >
                          {getJobsByStatus(column.id).map((job, index) => (
                            <JobCard key={job._id} job={job} index={index} />
                          ))}
                          {provided.placeholder}
                          
                          {/* Add New Job Button */}
                          <AddJobButton stage={column.id} />
                        </div>
                      </div>
                    )}
                  </Droppable>
                ))}
              </div>
            </DragDropContext>
          </div>

          {/* Job Details Modal */}
          <JobDetailsModal
            open={jobDetailsModalOpen}
            onClose={handleCloseJobDetailsModal}
            onSubmit={handleJobSubmit}
            job={viewingJob}
            defaultStage={selectedStage}
          />

          {/* Snackbar for notifications */}
          {snackbar.open && (
            <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-right duration-300">
              <div className={`
                px-4 py-3 rounded-lg shadow-lg border flex items-center gap-3
                ${snackbar.severity === 'success' ? 'bg-green-900 bg-opacity-80 border-green-500 text-green-200' : ''}
                ${snackbar.severity === 'error' ? 'bg-red-900 bg-opacity-80 border-red-500 text-red-200' : ''}
                ${snackbar.severity === 'info' ? 'bg-blue-900 bg-opacity-80 border-blue-500 text-blue-200' : ''}
              `}>
                <span className="text-sm font-medium">{snackbar.message}</span>
                <button
                  onClick={handleCloseSnackbar}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </PageLayout>
      </div>
    </>
  );
}

export default JobTracker; 